/* eslint-disable react/prop-types */
import { Tag } from '@sketch-note/design-system/src/components/tags';
import { ChevronDownIcon, ProfileImageIcon } from '@sketchnote/icons';
import * as React from 'react';
import {
  components as SelectComponents,
  GroupTypeBase,
  OptionTypeBase,
  SingleValueProps,
  StylesConfig,
} from 'react-select';

import { Avatar, AvatarGroup } from '../avatar';

type OptionType = OptionTypeBase & { label: string; value: string };
type GroupType = GroupTypeBase<OptionType>;

export const creationSelectStyles: StylesConfig<OptionType, boolean, GroupType> = {
  control: (styles) => ({
    ...styles,
    backgroundColor: '#F6F7F9',
    padding: '0.6rem 1.2rem 0.6rem 0px',
    borderRadius: '6px',
    borderColor: '#E2E4E9',
    fontWeight: 400,
    fontSize: '1.6rem',
    color: '#131720',
    width: '100%',
  }),
  menu: (styles) => ({
    ...styles,
    borderRadius: '6px',
    width: '100%',
    marginTop: '0.7rem',
    background: '#fff',
    boxShadow: '0px 2px 12px rgba(19, 23, 32, 0.08)',
    padding: '0.6rem 0.6rem',
  }),
  menuList: (styles) => ({
    ...styles,
    padding: '0px',
  }),
  group: (styles) => ({
    ...styles,
    padding: '0 0 0.5rem 0',
  }),
  groupHeading: (styles) => ({
    ...styles,
    fontWeight: 400,
    color: '#878A92',
    fontSize: '14px',
    padding: '0.6rem 0.8rem',
    textTransform: 'none',
  }),
  option: (styles, { isDisabled, isFocused, isSelected }) => {
    return {
      ...styles,
      backgroundColor: isDisabled
        ? '#F6F7F9'
        : isSelected
        ? '#EEF1FC'
        : isFocused
        ? '#F6F7F9'
        : 'inherit',
      padding: '0.6rem 0.6rem 0.6rem 0.4rem',
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '20px',
      marginBottom: '0.5rem',
      color: isDisabled ? '#ccc' : '#131720',
      cursor: isDisabled ? 'not-allowed' : 'default',
      borderRadius: '6px',
      ':active': {
        ...styles[':active'],
        backgroundColor: '#EEF1FC',
      },
    };
  },
  multiValue: (styles) => ({
    ...styles,
    margin: '0',
    backgroundColor: 'inherit',
  }),
  multiValueRemove: (styles) => ({
    ...styles,
    ':hover': {
      background: '#EEF1FC',
    },
  }),
};

export const CreationSelectPlaceholder = ({
  label = 'Select from templates in use',
}: {
  label?: string;
}): JSX.Element => <div className="flex items-center">&nbsp;&nbsp;{label}</div>;

export const CreationSelectDropDownIndicator = () => <ChevronDownIcon className="text-gray-500" />;

export const CreationSelectSingleValue:
  | React.ComponentType<SingleValueProps<OptionType, GroupType>>
  | undefined = (props) => (
  <div className="flex items-center">
    <Avatar src={props.data.avatar} type="photo" name={props.data.label} />
    <span className="ml-8">{props.data.label}</span>
  </div>
);

export const CreationSelectOption: any = (props) => (
  <SelectComponents.Option {...props}>
    <div>
      <div className="flex items-center">
        <Avatar src={props.data.avatar} type="photo" size="md" name={''} />
        <span className="mx-8">{props.data.label}</span>
        <Tag color={props.data.tag?.color}>{props.data.tag?.label}</Tag>
      </div>
      <div className="flex">
        <span className="font-regular text-xs text-gray-500 ml-38 block">
          Created by{' '}
          {typeof props.data?.createdBy === 'string'
            ? props.data?.createdBy
            : props.data?.createdBy?.firstname}{' '}
          {props.data?.sharedBy
            ? `| Shared with you by ${
                typeof props.data?.sharedBy === 'string'
                  ? props.data.sharedBy
                  : props.data.sharedBy?.firstname
              }`
            : ''}
        </span>
        {props.data.sharedWith?.length ? (
          <AvatarGroup maxCount={6} className="ml-12 mt-4">
            {props.data.sharedWith.map((obj, index) => (
              <Avatar
                name={obj.member.firstname}
                src={obj.member.avatar}
                type="photo"
                size="xs"
                key={`${obj.member.name}-${index}`}
                wrapperClassName="-mr-8 border-2 border-white"
              />
            ))}
          </AvatarGroup>
        ) : null}
      </div>
    </div>
  </SelectComponents.Option>
);
